import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { API_BASE_URL } from '../env'

interface PagePermission {
  page: string
  canRead: boolean
  canWrite: boolean
}

interface ImpersonationInfo {
  isImpersonating: boolean
  realAdmin?: {
    email: string
    name: string
  }
}

interface User {
  id: number
  email: string
  name: string
  avatarUrl?: string
  role?: string // Legacy: primary role
  roles?: string[] // Array of all roles
  accessiblePages?: string[] // Legacy: just page names
  pagePermissions?: PagePermission[] // New: page permissions with read/write info
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  impersonation: ImpersonationInfo | null
  login: () => void
  logout: () => void
  hasPageAccess: (page: string) => boolean
  hasWriteAccess: (page: string) => boolean
  startImpersonation: (userId: number) => Promise<void>
  stopImpersonation: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [impersonation, setImpersonation] = useState<ImpersonationInfo | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    checkAuthStatus()
  }, [])

  const checkAuthStatus = async () => {
    try {
      const response = await api.get('/auth/me')
      if (response.data.success) {
        setUser(response.data.user)
        setImpersonation(response.data.impersonation || null)
      }
    } catch (error) {
      console.log('Not authenticated')
    } finally {
      setIsLoading(false)
    }
  }

  const login = () => {
    window.location.href = `${API_BASE_URL}/auth/google`
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      setUser(null)
      setImpersonation(null)
      navigate('/login')
    }
  }

  const startImpersonation = async (userId: number) => {
    try {
      const response = await api.post(`/auth/impersonate/${userId}`)
      if (response.data.success) {
        // Refresh auth status to get impersonated user info
        await checkAuthStatus()
        // Reload the page to reset all state
        window.location.href = '/dashboard'
      }
    } catch (error: any) {
      console.error('Impersonation error:', error)
      throw new Error(error.response?.data?.message || 'Failed to start impersonation')
    }
  }

  const stopImpersonation = async () => {
    try {
      const response = await api.post('/auth/stop-impersonation')
      if (response.data.success) {
        // Refresh auth status to get admin user info back
        await checkAuthStatus()
        // Reload the page to reset all state
        window.location.href = '/user-management'
      }
    } catch (error: any) {
      console.error('Stop impersonation error:', error)
      throw new Error(error.response?.data?.message || 'Failed to stop impersonation')
    }
  }

  const hasPageAccess = (page: string): boolean => {
    if (!user) return false
    
    // Use new pagePermissions if available
    if (user.pagePermissions && user.pagePermissions.length > 0) {
      const permission = user.pagePermissions.find(p => p.page === page)
      return permission?.canRead ?? false
    }
    
    // Fallback to legacy accessiblePages (backward compatibility)
    if (!user.accessiblePages || user.accessiblePages.length === 0) {
      // If no permissions loaded yet, allow all pages (backward compatibility)
      return true
    }
    return user.accessiblePages.includes(page)
  }

  const hasWriteAccess = (page: string): boolean => {
    if (!user) return false
    
    // Use new pagePermissions if available
    if (user.pagePermissions && user.pagePermissions.length > 0) {
      const permission = user.pagePermissions.find(p => p.page === page)
      return permission?.canWrite ?? false
    }
    
    // Fallback: if using legacy accessiblePages, assume write access if page is accessible
    // (for backward compatibility during migration)
    return hasPageAccess(page)
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, impersonation, login, logout, hasPageAccess, hasWriteAccess, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
